import { buildBokadirektPayReconcileUrl } from '@/features/payments/utils/paymentUrls';
import { navigate } from '@/helpers/embed';
import { checkPaymentConfirmed } from '@/helpers/adyen';
import { trackMpEvent } from '@/helpers/analyticsTS';
import { OnPaymentCompletedData } from '@adyen/adyen-web/dist/types/components/types';
import { useCallback } from 'react';
import { PaymentMethod, paymentsServices, StoredPaymentMethod } from '../payments.services';
import { SessionState } from '../payments.types';

type UseOnCompleteAdyenCheckoutParams = {
  session: SessionState | null;
  resourceId: string | number;
  resourceType?: 'booking' | 'bundle';
  storedPaymentMethods: StoredPaymentMethod[];
  paymentMethods: PaymentMethod[];
  manualSetPaymentMethods: (methods: {
    paymentMethods: PaymentMethod[];
    storedPaymentMethods: StoredPaymentMethod[];
  }) => void;
  onSubmitting: () => void;
  onCardAdded: (card: StoredPaymentMethod) => void;
  onNoNewCardFound?: () => void;
  onBeforeRedirect?: () => void;
  onError?: (error: unknown) => void;
  trackingSessionId?: string;
};

export function useOnCompleteAdyenCheckout({
  session,
  resourceId,
  resourceType = 'booking',
  storedPaymentMethods,
  paymentMethods,
  manualSetPaymentMethods,
  onSubmitting,
  onCardAdded,
  onNoNewCardFound,
  onBeforeRedirect,
  onError,
  trackingSessionId,
}: UseOnCompleteAdyenCheckoutParams) {
  return useCallback(
    async (result: OnPaymentCompletedData) => {
      if (!checkPaymentConfirmed(result)) {
        onError?.(new Error(`Payment failed with result code: ${result.resultCode}`));
        return;
      }
      if (!session?.isCard) {
        onBeforeRedirect?.();
        navigate(
          buildBokadirektPayReconcileUrl({
            resourceType,
            resourceId,
            checkoutRef: session?.checkoutRef,
            session: { id: session?.id, result: result.sessionResult },
          }),
        );
        return;
      }
      onSubmitting();
      trackMpEvent({
        name: 'add_payment_card_flow',
        properties: {
          step: 'success',
          ...(resourceType === 'bundle'
            ? { buy_bundle_session_id: trackingSessionId }
            : { booking_session_id: trackingSessionId }),
        },
      });
      try {
        const storedCards = await paymentsServices.getTokenizedCards();
        manualSetPaymentMethods({ paymentMethods, storedPaymentMethods: storedCards.data });
        const oldCardIds = new Set(storedPaymentMethods.map((card) => card.id));
        const newCard = storedCards.data.find((storedCard) => !oldCardIds.has(storedCard.id));
        if (newCard) {
          onCardAdded(newCard);
        } else {
          onNoNewCardFound?.();
        }
      } catch (error) {
        onError?.(error);
      }
    },
    [
      session,
      resourceId,
      resourceType,
      storedPaymentMethods,
      paymentMethods,
      manualSetPaymentMethods,
      onSubmitting,
      onCardAdded,
      onNoNewCardFound,
      onBeforeRedirect,
      onError,
      trackingSessionId,
    ],
  );
}
